import * as pdfjsLib from 'pdfjs-dist'
import type { PdfCrop } from '../types/pdfCrop'

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js'

type PdfDocument = Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']>

const TARGET_PAGE_WIDTH = 3000

const documentCache = new Map<string, Promise<PdfDocument>>()
const pageCache = new Map<string, Promise<HTMLCanvasElement>>()
const cropCache = new Map<string, Promise<string>>()

function loadDocument(url: string): Promise<PdfDocument> {
  let cached = documentCache.get(url)
  if (!cached) {
    cached = pdfjsLib.getDocument({ url }).promise
    documentCache.set(url, cached)
  }
  return cached
}

function renderPage(url: string, pageNumber: number): Promise<HTMLCanvasElement> {
  const key = `${url}#${pageNumber}`
  let cached = pageCache.get(key)
  if (!cached) {
    cached = (async () => {
      const doc = await loadDocument(url)
      const page = await doc.getPage(pageNumber)
      const base = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: TARGET_PAGE_WIDTH / base.width })
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Canvas unavailable')
      await page.render({ canvasContext: context, viewport }).promise
      return canvas
    })()
    pageCache.set(key, cached)
  }
  return cached
}

/** Renders a region of a PDF page and returns it as an image data URL. */
export function getPdfCrop(url: string, crop: PdfCrop): Promise<string> {
  const key = `${url}#${crop.page}:${crop.x},${crop.y},${crop.w},${crop.h}`
  let cached = cropCache.get(key)
  if (!cached) {
    cached = (async () => {
      const source = await renderPage(url, crop.page)
      const sx = Math.round(crop.x * source.width)
      const sy = Math.round(crop.y * source.height)
      const sw = Math.round(crop.w * source.width)
      const sh = Math.round(crop.h * source.height)
      const canvas = document.createElement('canvas')
      canvas.width = sw
      canvas.height = sh
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Canvas unavailable')
      context.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh)
      return canvas.toDataURL('image/jpeg', 0.92)
    })()
    cached.catch(() => cropCache.delete(key))
    cropCache.set(key, cached)
  }
  return cached
}
